import { defineChain } from "viem";
import { bsc, bscTestnet } from "viem/chains";

const requestedNetwork = import.meta.env.VITE_NETWORK as string | undefined;
if (
  import.meta.env.MODE !== "test"
  && requestedNetwork !== "mainnet"
  && requestedNetwork !== "testnet"
  && requestedNetwork !== "localnet"
) {
  throw new Error("VITE_NETWORK must be explicitly set to mainnet, testnet, or localnet");
}

export const isTestnet = requestedNetwork === "testnet";
export const isLocal = requestedNetwork === "localnet";
if (!import.meta.env.DEV && isLocal) {
  throw new Error("VITE_NETWORK=localnet is only allowed for local development");
}
export const localChain = defineChain({
  id: 31337,
  name: "OnChainSage Localnet",
  nativeCurrency: { name: "Local BNB", symbol: "tBNB", decimals: 18 },
  rpcUrls: { default: { http: ["http://127.0.0.1:8545"] } }
});
export const appChain = isLocal ? localChain : isTestnet ? bscTestnet : bsc;
export const networkLabel = isLocal ? "本地测试链" : isTestnet ? "BSC 测试网" : "BSC 主网";
export const publicBscRpcUrl = isLocal ? "http://127.0.0.1:8545" : isTestnet
  ? "https://bsc-prebsc-dataseed.bnbchain.org"
  : "https://bsc-dataseed.bnbchain.org";
export const bscRpcUrl = (import.meta.env.VITE_BSC_RPC_URL as string | undefined)
  ?? publicBscRpcUrl;

const configuredAddress = (key: string, testnetValue: string | undefined, mainnetValue: string) => {
  const value = isTestnet || isLocal ? testnetValue : mainnetValue;
  if (!value || !/^0x[a-fA-F0-9]{40}$/.test(value)) {
    throw new Error(`${key} is missing or invalid for ${networkLabel}`);
  }
  return value as `0x${string}`;
};

export const treasuryAddress = configuredAddress(
  "VITE_TREASURY_ADDRESS",
  import.meta.env.VITE_TREASURY_ADDRESS,
  (import.meta.env.VITE_TREASURY_ADDRESS as string | undefined)
    ?? "0x3784adb01bb30929c7d74e7a1be8511fe4d6f598"
);
export const usdtAddress = configuredAddress(
  "VITE_USDT_CONTRACT_ADDRESS",
  import.meta.env.VITE_USDT_CONTRACT_ADDRESS,
  (import.meta.env.VITE_USDT_CONTRACT_ADDRESS as string | undefined)
    ?? "0x55d398326f99059fF775485246999027B3197955"
);
export const directContractAddress = configuredAddress(
  "VITE_DIRECT_REWARD_CONTRACT_ADDRESS",
  import.meta.env.VITE_DIRECT_REWARD_CONTRACT_ADDRESS,
  (import.meta.env.VITE_DIRECT_REWARD_CONTRACT_ADDRESS as string | undefined)
    ?? "0xeb4e64a4acd143a3fbdff0e437cb2e303dbb88ff"
);
export const feeContractAddress = configuredAddress(
  "VITE_FEE_DIVIDEND_CONTRACT_ADDRESS",
  import.meta.env.VITE_FEE_DIVIDEND_CONTRACT_ADDRESS,
  (import.meta.env.VITE_FEE_DIVIDEND_CONTRACT_ADDRESS as string | undefined)
    ?? "0x62060bc4b99412b946f00cd84525302f20632bf4"
);
const isAddress = (value: string) => /^0x[a-fA-F0-9]{40}$/.test(value);
export const hasConfiguredContracts = [usdtAddress, treasuryAddress, directContractAddress, feeContractAddress]
  .every(isAddress);

const optionalAddress = (value: string | undefined) => value && isAddress(value)
  ? value as `0x${string}`
  : undefined;

export const marketParticipationAddress = optionalAddress(
  import.meta.env.VITE_MARKET_PARTICIPATION_CONTRACT_ADDRESS as string | undefined
);
export const iageAddress = optionalAddress(
  import.meta.env.VITE_IAGE_CONTRACT_ADDRESS as string | undefined
);
export const sageAddress = optionalAddress(
  import.meta.env.VITE_SAGE_CONTRACT_ADDRESS as string | undefined
);
export const marketAssetGatewayAddress = optionalAddress(
  import.meta.env.VITE_MARKET_ASSET_GATEWAY_CONTRACT_ADDRESS as string | undefined
);
export const marketPayoutVaultAddresses = String(
  import.meta.env.VITE_MARKET_PAYOUT_VAULT_ADDRESSES ?? ""
).split(",").map((value) => value.trim()).filter(Boolean)
  .map((value) => optionalAddress(value))
  .filter((value): value is `0x${string}` => Boolean(value));
export const hasMarketParticipationContracts = Boolean(marketParticipationAddress && iageAddress);
export const hasMarketAssetContracts = Boolean(
  marketAssetGatewayAddress && iageAddress && sageAddress
);
export const hasMarketRewardContracts = Boolean(
  marketPayoutVaultAddresses.length === 10 && iageAddress && sageAddress
);
